import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useState, useMemo, useEffect } from 'react';
import { FormHelperText } from '@mui/material';

import { IUnregisteredStepSix } from '../unregistered-steps-types';
import { UnregisteredStep4Style } from '../step-4/unregistered-4-style';
import { logData } from '../../../../../utils/debug.util';
import { hideModal } from '../../../../../store/slices/generalSlice';
import { RootState } from '../../../../../store/rootReducer';
import { DestinationSelection } from '../../../../../models/assistance.model';
import useGeocoding from '../../../../../hooks/useGeocoding/useGeocoding';
import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import { FormLayout } from '../../../../../global-styles/layout';
import { theme } from '../../../../../global-styles/colors';
import { getErrorMessage } from '../../../../../constants/errorMessages';
import { BodySmallText, Heading4Text } from '../../../../../components/typography/typography';
import { LocationOption } from '../../../../../components/google-maps/google-maps-types';
import GoogleMaps, {
  DEFAULT_ADDRESS,
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
} from '../../../../../components/google-maps/GoogleMaps';
import RadioCard from '../../../../../components/common/radio-card/radio-card';
import ModalContainer from '../../../../../components/common/modal-container/modal-container';
import ControlledInput from '../../../../../components/common/forms/ControlledInput';
import ControlledAutoComplete from '../../../../../components/common/forms/ControlledAutoComplete';
import { locationIcons } from '../../../../../assets/icons/icons';
import { fetchDetails } from '../../../../../api/fetchLocation';

type Option = {
  label: string;
  value: string;
};

const isValidOption = (option: unknown): option is Option => {
  return !!option && (option as Option).value !== undefined;
};

const UnregisteredStep6: React.FC<IUnregisteredStepSix> = ({ form }) => {
  const { t } = useTranslation(['common', 'auth']);
  const dispatch = useDispatch();
  const [iconLocation, setIconLocation] = useState(locationIcons['00000016']);

  const providerCode = useSelector((state: RootState) => state.general.provider);

  // Form
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    clearErrors,
  } = form;

  const [latitude, longitude, locationDescription, error, selectDestination] = getValues([
    'latitude',
    'longitude',
    'locationDescription',
    'error',
    'selectDestination',
  ]);

  const modal = useSelector((state: RootState) => state.general.modal);

  // Coords logic
  const [locationSearch, setLocationSearch] = useState<string>(() => {
    return `${latitude || DEFAULT_LATITUDE}, ${longitude || DEFAULT_LONGITUDE}`;
  });

  const newCoords = useDebounce(locationSearch, 1000);

  const { predictions, locationData } = useGeocoding({
    input: newCoords,
    queryOptions: { refetchOnWindowFocus: false },
    location: { lat: latitude, lng: longitude },
  });

  const [selectedLocation, setSelectedLocation] = useState<LocationOption | undefined>(() => {
    if (latitude && longitude) {
      return { label: '', value: [latitude, longitude] };
    }

    return undefined;
  });

  const [selectedDefaultAddress, setSelectedDefaultAddress] = useState<boolean>(false);

  const locationAddress = useMemo(() => {
    let addressString = '';

    if (selectedLocation?.formattedAddress) {
      addressString = selectedLocation.formattedAddress;
    } else if (selectedLocation?.label) {
      addressString = selectedLocation.label;
    } else if (locationData?.results?.[0].formatted_address && [latitude, longitude].every((coord) => coord !== 0)) {
      addressString = locationData.results[0].formatted_address;
    } else {
      addressString = locationDescription;
    }

    setSelectedDefaultAddress(addressString === DEFAULT_ADDRESS);

    return addressString ?? '';
  }, [latitude, longitude, locationData?.results, locationDescription, selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      clearErrors('search');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  useEffect(() => {
    if (providerCode) {
      setIconLocation(locationIcons[providerCode as keyof typeof locationIcons]);
    }
  }, [providerCode]);

  const setNewLocation = (location: LocationOption) => {
    setSelectedLocation(location);
    logData('New location selected', location);
    setValue('latitude', location.value[0]);
    setValue('longitude', location.value[1]);
    setValue('locationDescription', location.formattedAddress || '');
  };

  const renderDestinationStep = () => {
    const step = getValues('step');
    switch (step) {
      case 1:
        return (
          <>
            <Heading4Text className="m-12px" weight={600}>
              {t('auth:unregistered.destination.decide.title')}
            </Heading4Text>
            <RadioCard
              id={'selected-destination-1'}
              dataCy={DestinationSelection.now.toString()}
              selectedValue={selectDestination === 1}
              data={{
                value: '1',
                title: t('auth:unregistered.destination.decide.now'),
              }}
              onClick={() => {
                setValue('selectDestination', 1);
              }}
            />
            <br />
            <RadioCard
              id={'selected-destination-2'}
              dataCy={DestinationSelection.later.toString()}
              selectedValue={selectDestination === 2}
              data={{
                value: '2',
                title: t('auth:unregistered.destination.decide.later'),
              }}
              onClick={() => {
                setValue('latitude', 0);
                setValue('longitude', 0);
                setValue('locationDescription', '');
                setValue('selectDestination', 2);
              }}
            />
            {error && (
              <FormHelperText className="error-block" error>
                * {t('auth:unregistered.destination.error')}
              </FormHelperText>
            )}
          </>
        );

      case 2:
        return (
          <>
            <Heading4Text className="m-12px" weight={600}>
              {t('auth:unregistered.destination.select.title')}
            </Heading4Text>
            <BodySmallText color={theme.type.secondary} className="m-24px">
              {t('auth:unregistered.destination.select.where-to')}
            </BodySmallText>
            <div className="search-container">
              <ControlledAutoComplete
                data-cy={'place-search'}
                options={
                  predictions?.map((place: google.maps.places.PlacePrediction) => ({
                    label: place.text.text,
                    value: place.placeId,
                  })) || []
                }
                onInputChange={(_, value, reason) => {
                  if (reason !== 'reset') {
                    setLocationSearch(value);
                  }
                }}
                label={t('common:label-inputs.search')}
                onChange={(_, option) => {
                  if (isValidOption(option)) {
                    fetchDetails(option.value, ['location', 'formattedAddress']).then((details) => {
                      const lat = details?.location?.lat();
                      const lng = details?.location?.lng();
                      if (lat && lng) {
                        const location: LocationOption = {
                          label: option.label,
                          value: [lat, lng],
                          formattedAddress: details?.formattedAddress || undefined,
                        };
                        setSelectedLocation(location);
                      }
                    });
                  }
                }}
                freeSolo
                filterOptions={(options) => {
                  return options;
                }}
                noOptionsText={t('common:common-text.no-options')}
                controllerProps={{
                  name: 'search',
                  control: control,
                  rules: {
                    required: !selectedLocation,
                  },
                }}
              />
              <FormHelperText className="error-block" error>
                {getErrorMessage(t, errors.search?.type)}
              </FormHelperText>
            </div>
            <div className="location-description">
              {locationAddress.length > 0 && <img src={iconLocation} alt="location" />}
              <div>{locationAddress.length > 0 && <BodySmallText weight={600}>{locationAddress}</BodySmallText>}</div>
            </div>
            <div className="map-container m-24px">
              <GoogleMaps
                coords={selectedLocation?.value}
                zoom={13}
                updateLocationFn={setNewLocation}
                showCenter={true}
                autoLocate={true}
              />
            </div>
            <FormLayout>
              <ControlledInput
                inputProps={{
                  multiline: true,
                  placeholder: t('auth:unregistered.step-3.location-information-placeholder'),
                  'data-cy': 'username',
                  variant: 'filled',
                  inputProps: {
                    maxLength: 250,
                  },
                }}
                controllerProps={{
                  control,
                  name: 'locationInformation',
                }}
              />
            </FormLayout>

            {/* ModalConfirm Position */}
            <ModalContainer
              fullScreen
              buttons={[
                {
                  className: 'primary',
                  onClick: () => {
                    setValue('locationDescription', locationAddress);
                    setValue('latitude', selectedLocation?.value[0] ?? 0);
                    setValue('longitude', selectedLocation?.value[1] ?? 0);
                    if (modal.onConfirm) modal.onConfirm(selectedLocation?.value);
                  },
                  label: t('auth:unregistered.step-3.modal.next'),
                  dataCy: 'confirm-position-btn',
                },
                {
                  className: 'secondary',
                  onClick: () => {
                    dispatch(hideModal());
                  },
                  label: t('auth:unregistered.step-3.modal.modify-location'),
                  dataCy: 'edit-phone-cancel-button',
                },
              ]}
              active={modal.name === 'confirm-destination-location' && selectedDefaultAddress === false}
              onClose={() => {
                dispatch(hideModal());
              }}
              title={t('auth:unregistered.destination.confirm-title')}
              text={t('auth:unregistered.destination.confirm-text')}
              dataCy="edit-phone-number-modal"
            >
              <div className="m-24px">
                {locationAddress.length > 0 && <BodySmallText weight={600}>{locationAddress}</BodySmallText>}
              </div>
              <GoogleMaps coords={selectedLocation?.value} zoom={13} showLocation />
            </ModalContainer>

            {/* ModalConfirm Position con dirección no válida */}
            <ModalContainer
              buttons={[
                {
                  className: 'primary',
                  onClick: () => {
                    dispatch(hideModal());
                  },
                  label: t('auth:unregistered.step-3.modal.modify-location'),
                  dataCy: 'modify-location-btn',
                },
              ]}
              active={modal.name === 'confirm-destination-location' && selectedDefaultAddress === true}
              onClose={() => {
                dispatch(hideModal());
              }}
              title={t('auth:unregistered.destination.invalid-location-modal.title')}
              text={t('auth:unregistered.destination.invalid-location-modal.text')}
              dataCy="edit-phone-number-modal"
            />
          </>
        );

      default:
        return <></>;
    }
  };

  return <UnregisteredStep4Style>{renderDestinationStep()}</UnregisteredStep4Style>;
};

export default UnregisteredStep6;
